<template>
  <div class="left-menu-wrapper fd-shell__sidebar" :class="{ 'is-collapsed': collapsed }">
    <v-icon
      class="toggle-icon"
      :icon="collapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left'"
      :style="toggleStyle"
      @click="toggle"
    />

    <div :class="['sidebar', { close: collapsed }]">
      <div class="logo-div" v-if="!collapsed">
        <div class="img-wrap">
          <v-img aspect-ratio="16/9" :src="logo.expanded" alt="logo" />
        </div>
      </div>
      <div class="logo-div2" v-else>
        <div class="img-wrap2">
          <v-img :src="logo.collapsed" alt="logo" />
        </div>
      </div>

      <ul class="nav-list">
        <li v-for="item in items" :key="item.id" :class="{ show: isOpen(item.id) }">
          <div
            v-if="item.children"
            class="icon-link"
            :class="{ 'active-item-sub': isActive(item) }"
            @click="toggleSubmenu(item.id)"
          >
            <div class="link">
              <img :src="isActive(item) ? item.activeIcon || item.icon : item.icon" alt="" />
              <span class="link-name">{{ item.title }}</span>
              <v-icon
                class="submenu-arrow"
                :icon="isOpen(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                color="#E5E7EB"
                size="18"
              />
            </div>
          </div>
          <ul v-if="item.children" :class="{ show: isOpen(item.id) }" class="sub-menu" @click.stop>
            <li v-for="child in item.children" :key="child.id">
              <router-link
                :to="child.to"
                class="sub-link"
                @click="markActiveMenu(item.title)"
              >
                <img :src="isChildActive(child) ? child.activeIcon || child.icon : child.icon" alt="" />
                <span :class="{ 'active-text-child': isChildActive(child) }">
                  {{ child.title }}
                </span>
              </router-link>
            </li>
          </ul>
          <div v-else class="icon-link" :class="{ 'active-item': isActive(item) }">
            <router-link :to="item.to" class="link" @click="markActiveMenu(item.title)">
              <img :src="isActive(item) ? item.activeIcon || item.icon : item.icon" alt="" />
              <span class="link-name" :class="{ 'active-text': isActive(item) }">
                {{ item.title }}
              </span>
            </router-link>
          </div>
        </li>
      </ul>

      <div class="sidebar-bottom">
        <div v-if="showSettings" class="settings-block">
          <div
            class="icon-link settings-link"
            :class="{ 'active-item-sub': settingsOpen || settingsChildActive }"
            @click="settingsOpen = !settingsOpen"
          >
            <div class="link">
              <img :src="settingsIcon" alt="" width="24" height="24" />
              <span class="link-name" v-if="!collapsed">Settings</span>
              <v-icon
                v-if="!collapsed"
                class="submenu-arrow"
                :icon="settingsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                color="#E5E7EB"
                size="18"
              />
            </div>
          </div>
          <ul v-if="settingsOpen && !collapsed" class="settings-sub-menu">
            <li v-for="link in settingsLinks" :key="link.id">
              <router-link :to="link.to" class="sub-link">
                <img :src="link.icon" alt="" width="24" height="24" />
                <span :class="{ 'active-text-child': isSettingsLinkActive(link) }">
                  {{ link.title }}
                </span>
              </router-link>
            </li>
          </ul>
        </div>

        <div v-if="showUsedSpace && !collapsed" class="fd-used-space">
          <div class="fd-used-space__title">Used space</div>
          <p class="fd-used-space__body">
            Your team has used 80% of your available space. Need more?
          </p>
          <div class="fd-used-space__bar" aria-hidden="true">
            <span />
          </div>
          <div class="fd-used-space__actions">
            <button type="button" @click="dismissUsedSpace">Dismiss</button>
            <button type="button" class="fd-used-space__upgrade">Upgrade plan</button>
          </div>
        </div>

        <v-menu location="top" :offset="8">
          <template #activator="{ props }">
            <div class="profile-row" :class="{ close: collapsed }" v-bind="props">
              <v-avatar size="40" class="profile-avatar">
                <v-img :src="profileImage" alt="profile" />
              </v-avatar>
              <div class="profile-meta" v-if="!collapsed">
                <span class="profile-name">{{ userFullName }}</span>
                <span class="profile-org">{{ tenantName || "Fluidsdata" }}</span>
              </div>
              <v-icon
                v-if="!collapsed"
                icon="mdi-logout"
                size="20"
                class="logout-icon"
                @click.stop="logout"
              />
            </div>
          </template>
          <v-card class="profile-menu-card">
            <v-card-text>
              <button type="button" class="profile-menu-item" @click="toggleDarkMode">
                <v-icon :icon="isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-weather-night'" size="18" />
                <span>{{ isDarkMode ? "Light mode" : "Dark mode" }}</span>
              </button>
              <button type="button" class="profile-menu-item" @click="logout">
                <v-icon icon="mdi-logout" size="18" />
                <span>Logout</span>
              </button>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useTenant } from "@app/composables/useTenant";
import { useAuth } from "@app/composables/useAuth";
import { useDarkMode } from "@app/composables/useDarkMode";
import { useNavigation } from "@app/composables/useNavigation";
import { useSidebar } from "@app/composables/useSidebar";

const OPEN_SUBMENUS_KEY = "leftMenuOpenSubmenus";

const route = useRoute();
const { userFullName, tenantName } = useTenant();
const { logout } = useAuth();
const { isDarkMode, toggleDarkMode } = useDarkMode();
const { items, settingsLinks, showSettings, isActive, isChildActive, markActiveMenu } =
  useNavigation();
const { collapsed, toggle } = useSidebar();

const USED_SPACE_KEY = "fd-used-space-dismissed";

const settingsOpen = ref(false);
const openSubmenus = ref(readOpenSubmenus());
const usedSpaceDismissed = ref(localStorage.getItem(USED_SPACE_KEY) === "1");
const showUsedSpace = computed(() => !usedSpaceDismissed.value);

const settingsIcon = require("@/assets/Svg/setting-2.svg");
const profileImage = require("@/assets/Images/Navbar/profile.png");
const logo = {
  expanded: require("@/assets/Images/LeftMenu/logo-dark.png"),
  collapsed: require("@/assets/Images/LeftMenu/logoicon-dark.png"),
};

const toggleStyle = computed(() => ({
  left: collapsed.value ? "48px" : "234px",
}));

const settingsChildActive = computed(() => {
  const path = route.path || "";
  return path.includes("/users") || path.includes("/roles");
});

function readOpenSubmenus() {
  try {
    const raw = localStorage.getItem(OPEN_SUBMENUS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function isOpen(id) {
  return openSubmenus.value.includes(id);
}

function toggleSubmenu(id) {
  if (openSubmenus.value.includes(id)) {
    openSubmenus.value = openSubmenus.value.filter((key) => key !== id);
  } else {
    openSubmenus.value = [...openSubmenus.value, id];
  }
  localStorage.setItem(OPEN_SUBMENUS_KEY, JSON.stringify(openSubmenus.value));
}

function isSettingsLinkActive(link) {
  return (route.path || "").startsWith(link.to);
}

function dismissUsedSpace() {
  usedSpaceDismissed.value = true;
  localStorage.setItem(USED_SPACE_KEY, "1");
}

watch(
  () => route.path,
  (path) => {
    items.value.forEach((item) => {
      if (item.children?.some((child) => path === child.to) && !isOpen(item.id)) {
        toggleSubmenu(item.id);
      }
    });
    if (path.includes("/users") || path.includes("/roles")) {
      settingsOpen.value = true;
    }
  },
  { immediate: true }
);
</script>
